import { create } from '@thoughtspot/logger';
import { BehaviorSubject } from 'rxjs';

const logger = create('SessionHydration');

// Latch defers cache-refresh kickoff until chart render-complete (or hover trigger).
type PendingRenderCompleteEntry = {
    rendered: boolean;
    run?: () => void;
};

const renderCompleteLatch = (() => {
    const map = new Map<string, PendingRenderCompleteEntry>();

    const getOrCreate = (vizId: string): PendingRenderCompleteEntry => {
        let entry = map.get(vizId);
        if (!entry) {
            entry = { rendered: false };
            map.set(vizId, entry);
        }
        return entry;
    };

    // Fire the queued callback at most once per register.
    const runOnce = (
        vizId: string,
        entry: PendingRenderCompleteEntry,
    ): void => {
        const callback = entry.run;
        if (!callback) {
            return;
        }
        // eslint-disable-next-line no-param-reassign
        entry.run = undefined;
        try {
            callback();
        } catch (err) {
            logger.warn(
                `[RenderCompleteLatch] callback for vizId=${vizId} threw`,
                err,
            );
        }
    };

    const register = (
        vizId: string,
        run: () => void,
        opts?: { fallbackMs?: number },
    ): void => {
        if (!vizId) {
            logger.warn(
                '[RenderCompleteLatch] register called without vizId; ignoring',
            );
            return;
        }
        const entry = getOrCreate(vizId);

        // Replace any prior queued callback.
        entry.run = run;

        // Race path: notify-before-register; fire synchronously.
        if (entry.rendered) {
            runOnce(vizId, entry);
        }
    };

    const notify = (vizId: string): void => {
        if (!vizId) {
            return;
        }
        const entry = getOrCreate(vizId);
        if (entry.rendered) {
            return;
        }
        entry.rendered = true;
        runOnce(vizId, entry);
    };

    const clear = (vizId?: string): void => {
        if (vizId === undefined) {
            map.clear();
            return;
        }
        const entry = map.get(vizId);
        if (!entry) {
            return;
        }
        map.delete(vizId);
    };

    return {
        register,
        notify,
        clear,
        // Test-only inspection of latch internals.
        inspect: {
            hasPending: (vizId: string): boolean => Boolean(map.get(vizId)?.run),
            isRendered: (vizId: string): boolean =>
                Boolean(map.get(vizId)?.rendered),
            size: (): number => map.size,
        },
    };
})();

// Session hydration lifecycle: tracks background refresh after cache hit.
export const SessionHydrationState = {
    /** Cache was not used (cache miss or caching disabled). */
    IDLE: 'IDLE',
    /** Data served from IndexedDB cache; background session refresh in progress. */
    HYDRATING: 'HYDRATING',
    /** Background refresh completed; session and answer data are fresh. */
    HYDRATED: 'HYDRATED',
    /** Error occurred during session hydration. */
    ERROR: 'ERROR',
} as const;

export type SessionHydrationState = typeof SessionHydrationState[keyof typeof SessionHydrationState];

export type SessionHydrationEvent = {
    state: SessionHydrationState;
    answerSessionId?: string;
    vizId?: string;
    error?: string;
};

const DEFAULT_EVENT: SessionHydrationEvent = {
    state: SessionHydrationState.IDLE,
};

// Per-vizId subject map for hydration events.
const subjectMap = new Map<string, BehaviorSubject<SessionHydrationEvent>>();

// Get or create a BehaviorSubject for a vizId (starts with default IDLE state).
export function getSessionHydrationSubject(
    vizId: string,
): BehaviorSubject<SessionHydrationEvent> {
    let subject = subjectMap.get(vizId);
    if (!subject) {
        subject = new BehaviorSubject<SessionHydrationEvent>(DEFAULT_EVENT);
        subjectMap.set(vizId, subject);
    }
    return subject;
}

/** Drop latch state for one vizId, or all of them when called with no arg. */
export const clearSessionLatch = (vizId?: string): void =>
    renderCompleteLatch.clear(vizId);

// Emit hydration event; vizId is injected from first arg, not the payload.
export function emitSessionHydrationEvent(
    vizId: string,
    event: Omit<SessionHydrationEvent, 'vizId'>,
): void {
    logger.debug(
        `[${event.state}] vizId=${vizId}${
            event.answerSessionId
                ? ` answerSessionId=${event.answerSessionId}`
                : ''
        }${event.error ? ` error=${event.error}` : ''}`,
    );
    getSessionHydrationSubject(vizId).next({ ...event, vizId });
}

// Cleanup subject on viz unmount; also clears latch state.
export function cleanupSessionHydrationSubject(vizId: string): void {
    const subject = subjectMap.get(vizId);
    if (subject) {
        logger.debug(`Cleaning up subject for vizId=${vizId}`);
        subject.complete();
        subjectMap.delete(vizId);
    } else {
        logger.warn(
            `cleanupSessionHydrationSubject called for unknown vizId=${vizId}`,
        );
    }
    clearSessionLatch(vizId);
}

// Cleanup all subjects on liveboard unmount; wipes all latch state.
export function clearAllSessionHydrationSubjects(): void {
    logger.debug(
        `Clearing all session hydration subjects (count=${subjectMap.size})`,
    );
    subjectMap.forEach(subject => subject.complete());
    subjectMap.clear();
    clearSessionLatch();
}

// Public latch API (delegates to renderCompleteLatch above).
export const registerCacheRefreshOnRenderComplete = renderCompleteLatch.register;
export const fireSessionLatch = renderCompleteLatch.notify;
// eslint-disable-next-line no-underscore-dangle
export const __INTERNAL_render_complete = renderCompleteLatch.inspect;
